import clsx from 'clsx';
import * as React from 'react';
import { Pressable, type PressEvent } from 'react-aria';

import { type Block, isTextRangeSelection } from '@gitbook/slate';
import type { BlockProps } from '@gitbook/slate-react';

import { getRealTimeColor } from '../../Users';

/**
 * Wrap a void block with selection controls.
 */
export const VoidBlockFrame = React.forwardRef(function VoidBlockFrameWithRef(
    props: BlockProps & {
        /** Whether the block can be interactively selected */
        selectable?: boolean;

        /** Callback when the view itself is pressed */
        onPress?: (event: PressEvent) => void;

        /**
         * Callback when the view is selected.
         * By default, moves the selection to the node
         */
        onSelect?: () => void;
        /**
         * Style to apply to the frame
         */
        className?: string;
    },
    ref: React.Ref<HTMLDivElement>
): React.ReactElement {
    const {
        onPress,
        onSelect = () => {
            editor.change((change) => {
                if (isTextRangeSelection(change.value.selection)) {
                    change.collapseToStartOf(node).focus();
                }
            });
        },
        node,
        children,
        editor,
        selections,
        isFocused,
        selectable = true,
    } = props;
    const { readOnly } = editor;

    const selectionBy = React.useMemo(() => {
        if (isFocused) {
            return undefined;
        }

        const other = selections.get(0);
        if (other) {
            return other.data.get('userId');
        }

        return undefined;
    }, [selections, isFocused]);

    const selectionColor = React.useMemo(() => {
        if (selectionBy) {
            return getRealTimeColor(selectionBy);
        }

        return null;
    }, [selectionBy]);

    return (
        <VoidSelectionFrame
            ref={ref}
            onPress={onPress}
            readOnly={readOnly}
            selectable={selectable}
            selected={isFocused || !!selectionBy}
            selectionColor={selectionColor?.base}
            onSelect={onSelect}
            className={props.className}
            node={node}
        >
            {children}
        </VoidSelectionFrame>
    );
});

/**
 * Frame around a component to allow selecting it with the mouse.
 */
export const VoidSelectionFrame = React.forwardRef(function VoidSelectionFrameWithRef(
    props: {
        selected: boolean;
        readOnly?: boolean;
        /** Whether the block can be interactively selected */
        selectable?: boolean;
        /** Color of the selection */
        selectionColor?: string;
        /** Callback when the content in it is pressed */
        onPress?: (event: PressEvent) => void;
        /** Callback when the selection frame is pressed */
        onSelect: () => void;
        /** Style to apply to the wrapper */
        className?: string;
        /** Content to wrap */
        children: React.ReactNode;
        /** Type of the node */
        node?: Block;
    },
    ref: React.Ref<HTMLDivElement>
): React.ReactElement {
    const {
        onPress,
        selected,
        readOnly = false,
        selectable = true,
        selectionColor,
        className,
        children,
        onSelect,
    } = props;

    if (readOnly) {
        return <>{children}</>;
    }

    const isGrid = props.node?.data?.get('view')?.type === 'grid';

    const innerView = (
        <div
            ref={ref}
            data-frame-selected={selected ? true : undefined}
            style={
                {
                    '--selection-color': selectionColor ?? 'var(--sp-color-primary-500)',
                    '--hover-color': 'var(--sp-color-frame-hover)',
                } as React.CSSProperties
            }
            className={clsx(
                'void-selection-frame relative',
                'after:pointer-events-none after:absolute after:z-20 after:select-none after:rounded after:border-solid after:content-[""]',
                isGrid
                    ? 'after:-left-2 after:-top-1 after:h-[calc(100%+.5rem)] after:w-full'
                    : 'after:-inset-1 after:h-[calc(100%+.5rem)] after:w-[calc(100%+.5rem)]',
                selected
                    ? 'after:border-2 after:border-[--selection-color]'
                    : 'after:border-4 after:border-transparent after:has-[>_svg_>_rect:hover]:border-[--hover-color]',
                className
            )}
        >
            {children}
            {selectable ? (
                <svg
                    className={clsx(
                        '-inset-1 pointer-events-none absolute z-20 select-none',
                        isGrid
                            ? '-left-2 -top-1 h-[calc(100%+.5rem)] w-full'
                            : '-inset-1 h-[calc(100%+.5rem)] w-[calc(100%+.5rem)]'
                    )}
                    xmlns="http://www.w3.org/2000/svg"
                    role="presentation"
                >
                    <rect
                        onClick={() => onSelect()}
                        role="button"
                        aria-label="Select block"
                        tabIndex={-1}
                        x="0.25rem"
                        y="0.25rem"
                        rx="0.25rem"
                        fill="none"
                        vector-effect="non-scaling-stroke"
                        className="h-[calc(100%-0.5rem)] w-[calc(100%-0.5rem)] cursor-pointer select-none stroke-[0.5rem] stroke-transparent [pointer-events:visibleStroke] hover:stroke-[--hover-color] hover:[stroke-opacity:0]"
                    />
                </svg>
            ) : null}
        </div>
    );

    return onPress ? <Pressable onPress={onPress}>{innerView}</Pressable> : innerView;
});
